import React from 'react';
import {
  Autocomplete, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, TextField, Tooltip,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';

export const DATE_PRESETS = ['last_30_days', 'last_3_months', 'last_6_months', 'last_12_months', 'ytd', 'all_time'];

/** Options for a select control: inline `options`, `options_source.values`, or `values`; items may be scalars or {value,label}. */
const controlOptions = (control) => {
  const src = control.options
    || (control.options_source && (control.options_source.values || control.options_source.options))
    || control.values
    || [];
  return (Array.isArray(src) ? src : []).map((o) =>
    (o && typeof o === 'object' ? { value: o.value, label: String(o.label != null ? o.label : o.value) }
      : { value: o, label: String(o) }));
};

const controlLabel = (t, control) =>
  control.label || t(`Dashboards.controls.names.${control.control_id}`, { defaultValue: control.control_id });

function DateRangeControl({ control, value, onChange }) {
  const { t } = useTranslation();
  const v = value || {};
  const isCustom = !v.relative && (v.from || v.to);
  const preset = isCustom ? 'custom' : (v.relative || 'all_time');
  const [draft, setDraft] = React.useState({ from: v.from || '', to: v.to || '' });

  React.useEffect(() => {
    setDraft({ from: v.from || '', to: v.to || '' });
  }, [v.from, v.to]);

  const onPreset = (p) => {
    if (p === 'custom') {
      const today = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 365 * 864e5).toISOString().slice(0, 10);
      onChange({ from, to: today });
    } else if (p === 'all_time') {
      onChange(null);
    } else {
      onChange({ relative: p });
    }
  };

  const commitDates = () => {
    if (draft.from !== (v.from || '') || draft.to !== (v.to || '')) {
      onChange({ from: draft.from || null, to: draft.to || null });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel>{controlLabel(t, control)}</InputLabel>
        <Select label={controlLabel(t, control)} value={preset} onChange={(e) => onPreset(e.target.value)}>
          {DATE_PRESETS.map((p) => (
            <MenuItem key={p} value={p}>{t(`Dashboards.controls.presets.${p}`)}</MenuItem>
          ))}
          <MenuItem value="custom">{t('Dashboards.controls.presets.custom')}</MenuItem>
        </Select>
      </FormControl>
      {preset === 'custom' && (
        <>
          <TextField size="small" type="date" label={t('Dashboards.controls.from')} InputLabelProps={{ shrink: true }}
            value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} onBlur={commitDates} />
          <TextField size="small" type="date" label={t('Dashboards.controls.to')} InputLabelProps={{ shrink: true }}
            value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} onBlur={commitDates} />
        </>
      )}
    </Box>
  );
}

function SelectControl({ control, value, onChange, multiple }) {
  const { t } = useTranslation();
  const options = controlOptions(control);
  const freeSolo = options.length === 0;
  const toOption = (val) => options.find((o) => o.value === val) || { value: val, label: String(val) };
  const selected = multiple
    ? (Array.isArray(value) ? value : []).map(toOption)
    : (value === null || value === undefined || value === '' ? null : toOption(value));

  return (
    <Autocomplete
      size="small"
      multiple={multiple}
      freeSolo={freeSolo}
      options={options}
      value={selected}
      limitTags={2}
      sx={{ minWidth: multiple ? 240 : 180, maxWidth: 360 }}
      getOptionLabel={(o) => (o && typeof o === 'object' ? o.label : String(o))}
      isOptionEqualToValue={(o, v) => o.value === (v && typeof v === 'object' ? v.value : v)}
      onChange={(_, next) => {
        if (multiple) {
          const vals = (next || []).map((o) => (o && typeof o === 'object' ? o.value : o));
          onChange(vals.length ? vals : null);
        } else {
          onChange(next === null || next === undefined ? null : (typeof next === 'object' ? next.value : next));
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label={controlLabel(t, control)} placeholder={t('Dashboards.controls.all')} />
      )}
    />
  );
}

function NumberRangeControl({ control, value, onChange }) {
  const { t } = useTranslation();
  const v = value || {};
  const [draft, setDraft] = React.useState({ min: v.min ?? '', max: v.max ?? '' });

  React.useEffect(() => {
    setDraft({ min: v.min ?? '', max: v.max ?? '' });
  }, [v.min, v.max]);

  const commit = () => {
    const min = draft.min === '' ? null : Number(draft.min);
    const max = draft.max === '' ? null : Number(draft.max);
    if (min === (v.min ?? null) && max === (v.max ?? null)) return;
    onChange(min === null && max === null ? null : { min, max });
  };
  const onKey = (e) => { if (e.key === 'Enter') commit(); };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <TextField size="small" type="number" sx={{ width: 120 }}
        label={`${controlLabel(t, control)} ${t('Dashboards.controls.min')}`}
        value={draft.min} onChange={(e) => setDraft((d) => ({ ...d, min: e.target.value }))} onBlur={commit} onKeyDown={onKey} />
      <TextField size="small" type="number" sx={{ width: 120 }}
        label={t('Dashboards.controls.max')}
        value={draft.max} onChange={(e) => setDraft((d) => ({ ...d, max: e.target.value }))} onBlur={commit} onKeyDown={onKey} />
    </Box>
  );
}

/**
 * Global filter controls. Changing a value is viewer-local: the parent only
 * re-queries the bound widgets' data, no version is created. "Save as default"
 * (owner only) persists the changed values as `set_control_value` EditOps.
 *
 * @param controls        spec.global_controls
 * @param values          { control_id: value } currently applied by the viewer
 * @param dirtyIds        control ids whose value differs from the spec default
 * @param onChange(controlId, value)
 * @param onSaveDefaults  persist dirty values (EditOps)
 * @param onReset         drop viewer-local values, back to spec defaults
 */
export default function ControlsBar({ controls, values, dirtyIds, canEdit, editPending, onChange, onSaveDefaults, onReset }) {
  const { t } = useTranslation();
  if (!Array.isArray(controls) || controls.length === 0) return null;
  const dirty = dirtyIds && dirtyIds.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {controls.map((c) => {
        const value = values[c.control_id];
        const set = (v) => onChange(c.control_id, v);
        const type = String(c.type || '').toLowerCase();
        if (type === 'date_range') return <DateRangeControl key={c.control_id} control={c} value={value} onChange={set} />;
        if (type === 'multi_select') return <SelectControl key={c.control_id} control={c} value={value} onChange={set} multiple />;
        if (type === 'number_range') return <NumberRangeControl key={c.control_id} control={c} value={value} onChange={set} />;
        return <SelectControl key={c.control_id} control={c} value={value} onChange={set} multiple={false} />;
      })}
      <Box sx={{ flexGrow: 1 }} />
      {dirty && (
        <Button size="small" startIcon={<RestartAltIcon />} onClick={onReset}>
          {t('Dashboards.controls.reset')}
        </Button>
      )}
      {dirty && canEdit && (
        <Tooltip title={t('Dashboards.controls.saveDefaultHint')}>
          <span>
            <Button size="small" variant="outlined" startIcon={<SaveOutlinedIcon />} disabled={editPending} onClick={onSaveDefaults}>
              {t('Dashboards.controls.saveDefault')}
            </Button>
          </span>
        </Tooltip>
      )}
    </Paper>
  );
}
